import { Alert, Button, Stack } from "@mantine/core";
import type { ReactNode } from "react";
import type { LoginPageFooterLink } from "../LoginPage";
import type { MessageState } from "../useLoginActions";
import type { LoginTr } from "../useLoginTranslations";
import { AuthSplitLayout } from "./AuthSplitLayout";
import { LoginFormFooter } from "./LoginForm/LoginFormFooter";

const EMAIL_PLACEHOLDER = "__CANUCCI_AUTH_EMAIL__";

export interface LoginEmailVerificationViewProps {
  userEmail: string;
  resendMessage: MessageState;
  resendLoading: boolean;
  onResend: () => void;
  onChangeAccount: () => void;
  logo?: ReactNode;
  footerAgreement?: ReactNode;
  footerSupportLink?: LoginPageFooterLink;
  /** When set, replaces the default instructions alert copy. */
  instructionsOverride?: ReactNode;
  tr: LoginTr;
}

export function LoginEmailVerificationView({
  userEmail,
  resendMessage,
  resendLoading,
  onResend,
  onChangeAccount,
  logo,
  footerAgreement,
  footerSupportLink,
  instructionsOverride,
  tr,
}: LoginEmailVerificationViewProps) {
  const [messageBeforeEmail, messageAfterEmail] = tr(
    "login.verifyEmail.message",
    "We sent a verification link to {{email}}. Open it to activate your account.",
    { email: EMAIL_PLACEHOLDER },
  ).split(EMAIL_PLACEHOLDER, 2);

  return (
    <AuthSplitLayout
      brandMode="verifyEmail"
      logo={logo}
      tr={tr}
      title={tr("login.verifyEmail.title", "Check Your Email")}
      subtitle={
        messageAfterEmail === undefined ? (
          messageBeforeEmail
        ) : (
          <>
            {messageBeforeEmail}
            <strong>{userEmail}</strong>
            {messageAfterEmail}
          </>
        )
      }
    >
      <Stack gap="md">
        <Alert variant="light" color="blue" radius="md">
          {instructionsOverride ??
            tr(
              "login.verifyEmail.instructions",
              "Check your inbox and click the link to activate your account. It may take a few minutes to arrive.",
            )}
        </Alert>
        {resendMessage ? (
          <Alert
            color={resendMessage.type === "success" ? "green" : resendMessage.type === "info" ? "blue" : "red"}
            radius="md"
          >
            {resendMessage.text}
          </Alert>
        ) : null}
        <Button onClick={onResend} fullWidth variant="light" loading={resendLoading} disabled={resendLoading}>
          {tr("login.verifyEmail.resend", "Resend verification email")}
        </Button>
        <Button onClick={onChangeAccount} fullWidth variant="outline">
          {tr("login.verifyEmail.changeAccount", "Sign in with a different account")}
        </Button>
        <LoginFormFooter footerAgreement={footerAgreement} footerSupportLink={footerSupportLink} />
      </Stack>
    </AuthSplitLayout>
  );
}
